import React, { useReducer, useState, useEffect, useCallback } from 'react';
import { LineReducer, Spinner, AlertBar, LineSetContext, LineUI, ButtonWithLoading, Button } from 'scorer-ui-kit';
import styled from 'styled-components';
import { setROIPoints } from '../apis';
import { LeftButton, RightAlignedContainer, AlertContainer, SpinnerDesign, CenterAlignedContainer } from '../Style';
import { useTranslation } from 'react-i18next';
import { ROI } from '../constants';

const Container = styled.div`
    width: 480px;
    margin-right: 40px;
    display: flex;
    flex-direction: column;
`;

const SpinnerContainer = styled.div`
  padding: 10px 0;
`;

interface IImageContainer {
  isReadOnly: boolean
}

const ImageContainer = styled.div<IImageContainer>`
  height: auto;
  width: 480px;
  position: relative;
  &::before {
    content: '';
    height: 100%;
    width: 480px;
    position: absolute;
    top: 0;
    left: 0;
    ${({isReadOnly})=>
      isReadOnly && 'z-index: 1;'
    }
    background: transparent;
  }
`;

const PointsViewer = (props: any) => {
  const { cameraName, rois, getCameraInfo, imageMessage, imageName, imageLoading } = props;
  const [state, dispatch] = useReducer(LineReducer, []);
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [loading, setLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [img, setImg] = useState<string>('');
  const [disabled, setDisabled] = useState<any>([]);
  const { t } = useTranslation(['DetectionSettings', 'LedStatus', 'Common']);


  const addPoints = useCallback(() => {
    let pointsData: any = [];
    let disabledROIs: any = [];
    rois.map((roi: ROI) => {
      const { pos_x, pos_y, id, enabled } = roi;
      const point = {
        name: id.toString(),
        points: [
          {
            x: pos_x,
            y: pos_y
          }
        ]
      };
      if (enabled) {
        pointsData.push(point);
      } else {
        disabledROIs.push(point);
      }
      return roi;
    });
    setDisabled(disabledROIs);
    dispatch({
      type: 'LOAD',
      state: pointsData
    });
  }, [rois]);

  const onSaveSettings = () => {
    setLoading(true);
    let newRois = [];
    const result = state.map(({ name, points }: any) => {
      return { id: parseInt(name), pos_x: points[0].x, pos_y: points[0].y };
    });
    const newDisabled =  disabled.map(({ name, points }: any) => {
      return { id: parseInt(name), pos_x: points[0].x, pos_y: points[0].y };
    });
    newRois.push(...newDisabled, ...result);
    const jsonData = {
      action: 'update_roi_position', data: {
        rois: newRois
      }
    };

    setROIPoints(cameraName, jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          getCameraInfo();
          setAlert({ key: new Date(), message: t('pointsUpdatedSuccessfully'), type: 'success' });
          setIsReadOnly(true);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
        setLoading(false);
      });
  };

  const onCancel = () => {
    let pointsData: any = [];
    rois.map((roi: ROI) => {
      const { pos_x, pos_y, id, enabled } = roi;
      if (enabled) {
        const point = {
          name: id.toString(),
          points: [
            {
              x: pos_x,
              y: pos_y
            }
          ]
        };
        pointsData.push(point);
      }
      return roi;
    });
    dispatch({
      type: 'LOAD',
      state: pointsData
    });  
    setIsReadOnly(true);
  };

  useEffect(() => {
    dispatch({
      type: 'LOAD',
      state: []
    });
    setDisabled([]);
    if (img !== '' && !imageLoading && !imgLoading) {
      addPoints();
    }
    
    return(() => {
      dispatch({
        type: 'LOAD',
        state: []
      });
      setDisabled([]);
    });
  }, [img, imageLoading, imgLoading, addPoints]);

  useEffect(() => {
    setImgLoading(true);
    setAlert(imageMessage);
    //setImg('https://www.w3schools.com/w3css/img_lights.jpg');
    if (!imageLoading) {
      let img = document.createElement('img');
      img.src = imageName;
      img.style.display = 'none';
      document.body.appendChild(img);
      img.onload = function () {
        setImgLoading(false);
        setImg(imageName);
      };
      img.onerror = function () {
        setImgLoading(false);
        setImg('');
        setAlert({ key: new Date(), message: t('imageNotAvailable'), type: 'info' });
        console.error('image failed');
      };
    } else {
      setImgLoading(false);
    }
  }, [imageName, imageLoading, imageMessage, t]);

  const changeCirclePointsBorder = useCallback(()=>{
    const ele = document.getElementsByTagName('circle');
    let i = ele.length;
    while (i--) {
      if (ele[i].getAttribute('cx') === null) {
        ele[i].setAttribute('r', '10');
        ele[i].setAttribute('stroke-width', '1.5');
      }
    }
  },[]);

  useEffect(()=>{
    changeCirclePointsBorder();  
  },[changeCirclePointsBorder,state]);


  return (
    <Container>
      {
        (imgLoading || imageLoading) &&
        <CenterAlignedContainer>
          <SpinnerContainer>
            <Spinner size='large' styling={SpinnerDesign} />
          </SpinnerContainer>
        </CenterAlignedContainer> 
      }
      {
        img !== '' &&
          <>
            <ImageContainer isReadOnly={isReadOnly}>
              <LineSetContext.Provider value={{ state, dispatch }}>
                <LineUI onSizeChange={()=>{
                  setTimeout(() => {
                    changeCirclePointsBorder();
                  });
                }} options={{ showSetIndex: false, showPoint: true }} src={(img + '?' + new Date())} />
              </LineSetContext.Provider>
            </ImageContainer>
            <RightAlignedContainer style={{ marginTop: '10px' }}>
              {
                isReadOnly ?
                <Button onClick={() => setIsReadOnly(false)} size='small'>{t('Common:edit')}</Button>
                :
                <>
                  <LeftButton design='secondary' size='small' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
                  <ButtonWithLoading loading={loading} size='small' onClick={onSaveSettings}>{t('Common:save')}</ButtonWithLoading>
                </>
              }
            </RightAlignedContainer>
          </>
      }
      {
        (alert.message !== '' &&
          <AlertContainer key={alert.key}>
            <AlertBar type={alert.type} message={alert.message} />
          </AlertContainer>
        )
      }
    </Container>
  );
};

export default PointsViewer;